import '../../Styles/BlueprintForm.css';

import { Form, Button, Input } from 'antd';
import React, { useState } from 'react';

import { capitalize } from 'src/utils/capitalize';

import { Fields } from '../fields';
import StyleDropdowns from '../Common/StyleDropdowns';
import { PROJECT_SCOPES, DEFAULT_STYLE_INFO_VALUES } from '../../constants/projectConstants';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from 'src/AuthContext';

import type { CreateType } from './types';
import type { StyleInfo, ProductLine } from '../../types/project';

export interface BlueprintFormValues extends Required<StyleInfo> {
  projectName?: string;
  versionName?: string;
  customerName?: string;
  customerEmail?: string;
  projectScope?: string;
  customProjectScope?: string;
  salesforceId?: string;
  productLine: ProductLine;
}

interface BlueprintFormProps {
  onSubmit: (values: BlueprintFormValues) => Promise<void>;
  loading: boolean;
  createType: CreateType;
  initialStyleInfo: StyleInfo | null;
}

const BlueprintForm: React.FC<BlueprintFormProps> = ({
  onSubmit,
  loading,
  createType,
  initialStyleInfo,
}) => {
  const [form] = Form.useForm();
  const projectScope = Form.useWatch('projectScope', form);

  const [activeStep, setActiveStep] = useState<'details' | 'style'>('details');

  const [isDetailsValid, setIsDetailsValid] = useState(false);

  const [searchParams] = useSearchParams();
  const { user } = useAuth();

  const passedEmail = searchParams.get('customerEmail') ?? undefined;
  const passedSfId = searchParams.get('sfId') ?? undefined;

  const showSalesforceIdInput = user.isInternal;
  const showCustomerNameInput = !user.isRetailRep || !passedSfId;

  // Monitor form changes
  const handleFieldsChange = () => {
    const requiredFields = [
      createType === 'project' ? 'projectName' : 'versionName',
      ...(showCustomerNameInput ? ['customerName'] : []),
      'projectScope',
      ...(projectScope?.toLowerCase() === 'custom' ? ['customProjectScope'] : []),
    ];

    const hasErrors = form
      .getFieldsError()
      .filter(({ name }) => requiredFields.includes(String(name[0])))
      .some((field) => field.errors.length > 0);

    const values = form.getFieldsValue(requiredFields);
    const allFieldsHaveValues = requiredFields.every((field) => {
      const value = values[field];
      return value !== undefined && value !== null && value !== '';
    });

    setIsDetailsValid(allFieldsHaveValues && !hasErrors);
    console.log('allFieldsHaveValues', allFieldsHaveValues, 'hasErrors', hasErrors, 'isDetailsValid', isDetailsValid);
  };

  const dropdownFields = [
    {
      name: 'projectScope',
      label: 'Project Scope',
      options: PROJECT_SCOPES,
      required: true,
      placeholder: 'Please select',
    },
  ];

  const nameField =
    createType === 'project'
      ? {
          name: 'projectName',
          label: 'Project Name',
          placeholder: 'Miami Condo',
          required: true,
        }
      : {
          name: 'versionName',
          label: 'Version Name',
          placeholder: 'Enter version name',
          required: true,
          rules: [
            { pattern: /^[^/]*$/, message: 'Version name cannot contain "/"' },
          ],
        };

  const customerNameField = {
    name: 'customerName',
    label: user.isMultifamily ? 'Developer' : 'Customer Name',
    placeholder: 'Williams',
    required: true,
  };

  return (
    <div className='container py-4 d-flex flex-direction-row blueprint-form-wrapper'>
      <div className='form-steps-wrapper'>
        <h6>{capitalize(createType)} information</h6>
        <div className='form-steps'>
          <div
            className={`form-step ${activeStep === 'details' ? 'active' : ''}`}
            onClick={() => setActiveStep('details')}
          >
            <p>Details</p>
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='6.67'
              height='12'
              viewBox='0 0 16.366 36.06'
              id='{{ element_id }}'
            >
              <path
                d='M0,14.813,17.683,0,35.1,14.587'
                transform='translate(15.387 0.482) rotate(90)'
                fill='none'
                stroke='#000'
                strokeMiterlimit='10'
                strokeWidth='1.5'
              />
            </svg>
          </div>
          <div
            className={`form-step ${activeStep === 'style' ? 'active' : ''}`}
            onClick={() => setActiveStep('style')}
          >
            <p>Style</p>
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='6.67'
              height='12'
              viewBox='0 0 16.366 36.06'
              id='{{ element_id }}'
            >
              <path
                d='M0,14.813,17.683,0,35.1,14.587'
                transform='translate(15.387 0.482) rotate(90)'
                fill='none'
                stroke='#000'
                strokeMiterlimit='10'
                strokeWidth='1.5'
              />
            </svg>
          </div>
        </div>
      </div>
      <Form
        className='main-form'
        form={form}
        layout='vertical'
        onFinish={onSubmit}
        onFieldsChange={handleFieldsChange}
        initialValues={
          initialStyleInfo || {
            ...DEFAULT_STYLE_INFO_VALUES,
            productLine: 'vista',
            customerEmail: passedEmail,
            salesforceId: passedSfId,
            ...(createType === 'project' && passedEmail ? { projectName: passedEmail } : {}),
          }
        }
      >
        {/* DETAILS STEP */}
        <div
          className='details-step'
          style={{ display: activeStep === 'details' ? 'flex' : 'none' }}
        >
          <div>
            <h3>Add {createType} details</h3>
            <Fields.Text config={nameField} />

            {showCustomerNameInput && (
              <Fields.Text key={customerNameField.name} config={customerNameField} />
            )}

            <Form.Item name='customerEmail' label='Customer Email'>
              <Input
                type='email'
                placeholder='customer@gmail.com'
                disabled={Boolean(passedEmail)}
              />
            </Form.Item>

            {showSalesforceIdInput && (
              <Form.Item name='salesforceId' label='Salesforce ID'>
                <Input placeholder='Enter Salesforce ID' disabled={Boolean(passedSfId)} />
              </Form.Item>
            )}
            {dropdownFields.map((field) => (
              <Fields.Dropdown key={field.name} config={field} />
            ))}
            {projectScope?.toLowerCase() === 'custom' && (
              <Fields.Text
                config={{
                  name: 'customProjectScope',
                  label: 'Custom Project Scope',
                  required: true,
                  placeholder: 'Enter custom project scope',
                }}
              />
            )}
          </div>
          <div className='form-button-wrapper'>
            <Button
              block
              type='default'
              style={{
                backgroundColor: isDetailsValid ? '#F27662' : '#d9d9d9',
                color: isDetailsValid ? 'white' : 'rgba(0, 0, 0, 0.25)',
                border: 'none',
                width: 'fit-content',
              }}
              disabled={!isDetailsValid}
              onClick={() => setActiveStep('style')}
            >
              Next Step
            </Button>
          </div>
        </div>

        {/* STYLE STEP */}
        <div style={{ display: activeStep === 'style' ? 'block' : 'none' }}>
          <StyleDropdowns />
        </div>

        {activeStep === 'style' && (
          <Form.Item className='mb-0 mt-3'>
            <div className='form-button-wrapper'>
              <Button
                type='primary'
                htmlType='submit'
                loading={loading}
                block
                style={{ width: 'fit-content' }}
                disabled={!isDetailsValid}
              >
                Continue to {capitalize(createType)} Setup
              </Button>
            </div>
          </Form.Item>
        )}
      </Form>
    </div>
  );
};

export default BlueprintForm;
